import { ReactNode, useEffect } from 'react'
import { useRouter } from 'next/router'
import { ICheckoutContext } from 'types/contexts'
import { Venue, VenueEvent } from 'types/model'

import { CheckoutContext } from '@contexts/checkout'
import { SessionContext } from '@contexts/session'
import { ThemeContext } from '@contexts/theme'
import { trackPageView } from '@lib/track'

import { VenuePageLayout } from '@modules/venue/layouts/venue-page-layout'
import { useAttribution } from '@hooks/attribution'
import GTM from '@components/gtm'
import { PostHogVenueGroupSync } from '@components/PostHogVenueGroupSync'
import { OrgInt, OrganizationIntegrationType } from 'types/model/orgInt'
import { normalizeSupportedLocale } from 'shared/src/utils/localeUtils'
import i18n from '@lib/i18n'

export interface VenuePageProviderProps {
  children: ReactNode
  checkoutValues?: Partial<ICheckoutContext>
  venue: Venue
  integrations: OrgInt[]
  event?: VenueEvent | null
  themeOverride?: string | null
  eventPageBanner?: boolean
  largePageBanner?: boolean
  embeddedWidget?: boolean
}

export function VenuePageProvider({
  children,
  venue,
  integrations,
  event,
  themeOverride,
  checkoutValues,
  eventPageBanner,
  largePageBanner,
  embeddedWidget,
}: VenuePageProviderProps) {
  const router = useRouter()
  const { affiliateId, attributionId } = useAttribution()
  const _themeOverride = ['light', 'theme-light'].includes(themeOverride ?? '')
    ? 'light'
    : ['dark', 'theme-dark'].includes(themeOverride ?? '')
      ? 'dark'
      : undefined
  const facebookPixelId =
    integrations.find(
      (integration) =>
        integration.type === OrganizationIntegrationType.FACEBOOK
    )?.facebook?.pixelId ?? venue.trackingFacebookPixelId

  useEffect(() => {
    const locale = normalizeSupportedLocale(venue.languageCode)
    void i18n.changeLanguage(locale)
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale
    }
  }, [venue.languageCode])

  useEffect(() => {
    if (!venue?.id) return
    void trackPageView({
      venueId: venue.id,
      eventId: event?.id,
    })
  }, [event?.id, router.asPath, venue?.id])

  return (
    <>
      <PostHogVenueGroupSync venue={venue} />
      <SessionContext>
        <CheckoutContext
          venue={venue}
          event={event}
          affiliateId={affiliateId}
          attributionId={attributionId}
          initialValues={checkoutValues}
          embeddedWidget={embeddedWidget}
        >
          <ThemeContext venue={venue} themeOverride={_themeOverride}>
            <VenuePageLayout
              venue={venue}
              facebookPixelId={facebookPixelId}
              event={event}
              eventPoster={eventPageBanner}
              largeBanner={largePageBanner}
              minimalLayout={embeddedWidget}
            >
              {children}
            </VenuePageLayout>
          </ThemeContext>
        </CheckoutContext>
      </SessionContext>
      <GTM integrations={integrations} />
    </>
  )
}
